/**
 * Trainer Meeting Tools — compose + list announcements.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createAnnouncement,
  fetchAnnouncements,
  patchAnnouncement,
} from "./liveCollaborationApi";
import type { LiveAnnouncement } from "./liveCollaborationTypes";
import { newCollabId } from "./liveCollaborationTypes";
import { persistWithRetry } from "./collabPersist";
import { mergeOlderById, oldestCreatedAt } from "./collabHistory";
import {
  sortByCreatedAtAsc,
  useStickToBottomScroll,
} from "./useStickToBottomScroll";

type Props = {
  sessionId: string;
  trainerId: string;
  trainerName: string;
  items: LiveAnnouncement[];
  /** Wait for room bootstrap attempt before any list REST. */
  hydrateReady?: boolean;
  /** Room collab/bootstrap succeeded — skip list REST even when empty. */
  skipHydrate?: boolean;
  joinedSince?: string;
  initialHasMore?: boolean;
  onItems: (items: LiveAnnouncement[]) => void;
  onPublish: (
    item: LiveAnnouncement,
    opts?: { broadcast?: boolean },
  ) => Promise<void>;
  onRemoveItem?: (
    announcementId: string,
    opts?: { broadcast?: boolean },
  ) => void;
};

export function TrainerAnnouncementsPanel({
  sessionId,
  trainerId,
  trainerName,
  items,
  hydrateReady = true,
  skipHydrate = false,
  joinedSince = "",
  initialHasMore = false,
  onItems,
  onPublish,
  onRemoveItem,
}: Props) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const [hasMore, setHasMore] = useState(Boolean(initialHasMore));
  const [loadingOlder, setLoadingOlder] = useState(false);
  const loadingOlderRef = useRef(false);

  useEffect(() => {
    setHasMore(Boolean(initialHasMore));
  }, [initialHasMore, sessionId]);

  useEffect(() => {
    if (!hydrateReady || skipHydrate || items.length > 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const page = await fetchAnnouncements(
          sessionId,
          joinedSince ? { since: joinedSince } : undefined,
        );
        if (!cancelled) {
          onItems(page.items);
          setHasMore(page.hasMore);
        }
      } catch (err) {
        console.error("fetchAnnouncements failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, hydrateReady, skipHydrate, joinedSince]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    const titleText = title.trim();
    const bodyText = body.trim();
    const nextTitleError = titleText ? null : "Title is required.";
    const nextBodyError = bodyText ? null : "Message is required.";
    setTitleError(nextTitleError);
    setBodyError(nextBodyError);
    if (nextTitleError || nextBodyError || busy) return;
    setBusy(true);
    const announcementId = newCollabId("ann");
    const wasPinned = pinned;
    const optimistic: LiveAnnouncement = {
      announcement_id: announcementId,
      session_id: sessionId,
      title: titleText,
      body: bodyText,
      author: {
        identity: trainerId,
        role: "trainer",
        display_name: trainerName,
      },
      pinned: wasPinned,
      status: "published",
      created_at: new Date().toISOString(),
      published_at: new Date().toISOString(),
    };
    setTitle("");
    setBody("");
    setPinned(false);
    setTitleError(null);
    setBodyError(null);
    void onPublish(optimistic);
    setBusy(false);
    void persistWithRetry(() =>
      createAnnouncement({
        sessionId,
        trainerId,
        trainerName,
        title: titleText,
        body: bodyText,
        pinned: wasPinned,
        announcementId,
      }),
    ).catch((err) => {
      console.error(err);
      onRemoveItem?.(announcementId);
      setTitle(titleText);
      setBody(bodyText);
      setPinned(wasPinned);
    });
  };

  const togglePin = async (a: LiveAnnouncement) => {
    const optimistic: LiveAnnouncement = {
      ...a,
      pinned: !a.pinned,
    };
    try {
      // Fan out pin/unpin instantly; persist after.
      await onPublish(optimistic);
      const item = await patchAnnouncement({
        sessionId,
        announcementId: a.announcement_id,
        action: a.pinned ? "unpin" : "pin",
      });
      await onPublish(item, { broadcast: false });
    } catch (err) {
      console.error(err);
      try {
        await onPublish(a);
      } catch {
        /* ignore */
      }
    }
  };

  const sorted = useMemo(() => {
    const pinnedRows = items.filter((a) => a.pinned);
    const rest = sortByCreatedAtAsc(items.filter((a) => !a.pinned));
    return [...pinnedRows, ...rest];
  }, [items]);

  const lastId = sorted.length
    ? String(sorted[sorted.length - 1]?.announcement_id || "")
    : "";
  const loadOlderRef = useRef<() => void>(() => {});
  const { scrollerRef, bottomRef, onScroll, restoreHeightRef } =
    useStickToBottomScroll([lastId, sorted.length], {
      onNearTop: () => loadOlderRef.current(),
    });

  const loadOlder = useCallback(() => {
    if (loadingOlderRef.current || !hasMore) return;
    const before = oldestCreatedAt(itemsRef.current, joinedSince);
    if (!before) {
      setHasMore(false);
      return;
    }
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    restoreHeightRef.current = scrollerRef.current?.scrollHeight ?? null;
    void (async () => {
      try {
        const page = await fetchAnnouncements(sessionId, { before });
        onItems(
          mergeOlderById(
            page.items,
            itemsRef.current,
            (a) => a.announcement_id,
          ),
        );
        setHasMore(page.hasMore);
      } catch (err) {
        console.error("load older announcements failed", err);
        restoreHeightRef.current = null;
      } finally {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    })();
  }, [hasMore, joinedSince, onItems, restoreHeightRef, scrollerRef, sessionId]);
  loadOlderRef.current = loadOlder;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
      }}
    >
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>
        Post an announcement to everyone in this session.
      </p>
      <input
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          if (titleError) setTitleError(null);
        }}
        placeholder="Title"
        aria-invalid={Boolean(titleError)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          borderRadius: 8,
          border: titleError
            ? "1px solid rgba(248,113,113,0.7)"
            : "1px solid rgba(255,255,255,0.12)",
          background: "rgba(0,0,0,0.25)",
          color: "#e2e8f0",
          padding: "8px 10px",
          fontSize: 13,
          marginBottom: titleError ? 4 : 8,
          flexShrink: 0,
        }}
      />
      {titleError ? (
        <p
          style={{
            margin: "0 0 8px",
            fontSize: 11,
            fontWeight: 600,
            color: "#fca5a5",
            flexShrink: 0,
          }}
        >
          {titleError}
        </p>
      ) : null}
      <textarea
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
          if (bodyError) setBodyError(null);
        }}
        placeholder="Message…"
        rows={3}
        aria-invalid={Boolean(bodyError)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          borderRadius: 8,
          border: bodyError
            ? "1px solid rgba(248,113,113,0.7)"
            : "1px solid rgba(255,255,255,0.12)",
          background: "rgba(0,0,0,0.25)",
          color: "#e2e8f0",
          padding: 10,
          fontSize: 13,
          fontFamily: "inherit",
          resize: "vertical",
          whiteSpace: "pre-wrap",
          flexShrink: 0,
        }}
      />
      {bodyError ? (
        <p
          style={{
            margin: "4px 0 0",
            fontSize: 11,
            fontWeight: 600,
            color: "#fca5a5",
            flexShrink: 0,
          }}
        >
          {bodyError}
        </p>
      ) : null}
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginTop: 8,
          fontSize: 12,
          color: "#94a3b8",
          flexShrink: 0,
        }}
      >
        <input
          type="checkbox"
          checked={pinned}
          onChange={(e) => setPinned(e.target.checked)}
        />
        Pin to top
      </label>
      <button
        type="button"
        disabled={busy}
        onClick={() => void submit()}
        style={{
          marginTop: 10,
          width: "100%",
          border: "none",
          borderRadius: 10,
          padding: "10px 12px",
          background: busy ? "#1e40af" : "#2563eb",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          cursor: busy ? "wait" : "pointer",
          opacity: busy ? 0.7 : 1,
          flexShrink: 0,
        }}
      >
        Post announcement
      </button>

      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="meeting-tool-hide-scrollbar"
        style={{
          flex: 1,
          minHeight: 120,
          overflowY: "auto",
          marginTop: 16,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {(hasMore || loadingOlder) ? (
          <button
            type="button"
            onClick={loadOlder}
            disabled={loadingOlder}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              color: "#64748b",
              fontSize: 11,
              padding: "4px 0 12px",
              cursor: loadingOlder ? "default" : "pointer",
            }}
          >
            {loadingOlder
              ? "Loading earlier announcements…"
              : "Load earlier announcements"}
          </button>
        ) : null}
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {sorted.map((a) => (
            <li
              key={a.announcement_id}
              style={{
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "rgba(255,255,255,0.04)",
                padding: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  {a.title}
                </p>
                <button
                  type="button"
                  onClick={() => void togglePin(a)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: a.pinned ? "#fbbf24" : "#94a3b8",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {a.pinned ? "Unpin" : "Pin"}
                </button>
              </div>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 13,
                  color: "#cbd5e1",
                  whiteSpace: "pre-wrap",
                }}
              >
                {a.body}
              </p>
            </li>
          ))}
        </ul>
        <div ref={bottomRef} aria-hidden style={{ height: 1 }} />
      </div>
    </div>
  );
}
